import * as React from 'react';
import { Query } from 'react-apollo';
import update from 'immutability-helper';
import { findMatchingRecord, findMatchingRecordIndex } from 'utils/arrayHelpers';
import UNCLAIMED_GROUPS_QUERY from './queries/UnclaimedGroupsQuery.gql';

import {
  UnclaimedGroupsQuery,
  UnclaimedGroupsQueryVariables,
  UnclaimedGroupsQuery_organizations,
  UnclaimedGroupsQuery_organizations_unclaimedGroups_unclaimedGroups as UnclaimedGroup,
} from './queries/__generated__/UnclaimedGroupsQuery';

import { GroupRow } from 'groups/components/GroupList/GroupListTable';
import { Props } from 'groups/containers/GroupList/GroupsListFetcher';

function translateUnclaimedGroup(group: UnclaimedGroup): GroupRow {
  return {
    uuid: group.uuid,
    className: group.className,
    createdAt: group.createdAt,
    initials: group.initials,
    owners: group.owners,
    participantsCount: group.participantCount,
  };
}

export default function UnclaimedGroupsListFetcher(props: Props) {
  const { orgUuid, query, sortDirection, sortType, handleError } = props;

  return (
    <Query<UnclaimedGroupsQuery, UnclaimedGroupsQueryVariables>
      query={UNCLAIMED_GROUPS_QUERY}
      variables={{
        orgUuid,
        first: 20,
        after: null,
        query,
        sortType,
        sortDirection,
      }}
      fetchPolicy="network-only">
      {({ data, error, fetchMore, loading, refetch }) => {
        if (error) {
          handleError();
          return null;
        }

        if (!data) {
          return props.children({ loading, hasNextPage: false });
        }

        const org = findMatchingRecord<UnclaimedGroupsQuery_organizations>(
          data.organizations,
          orgUuid
        );

        if (!org || !org.unclaimedGroups) {
          return props.children({ loading, hasNextPage: false });
        }

        const {
          unclaimedGroups,
          pageInfo: { endCursor, hasNextPage },
        } = org.unclaimedGroups;

        const handleFetchMore = () =>
          fetchMore({
            variables: {
              after: endCursor,
            },
            updateQuery: (prev, { fetchMoreResult }) => {
              if (!fetchMoreResult) {
                return prev;
              }

              const fetchMoreContents = findMatchingRecord(fetchMoreResult.organizations, orgUuid);
              const prevContents = findMatchingRecord(prev.organizations, orgUuid);
              const index = findMatchingRecordIndex(prev.organizations, orgUuid);

              if (!fetchMoreContents || !prevContents || index === undefined || index === null) {
                return prev;
              }

              return update(prev, {
                organizations: {
                  [index]: {
                    unclaimedGroups: {
                      unclaimedGroups: {
                        $set: [
                          ...prevContents.unclaimedGroups.unclaimedGroups,
                          ...fetchMoreContents.unclaimedGroups.unclaimedGroups,
                        ],
                      },
                      pageInfo: { $set: fetchMoreContents.unclaimedGroups.pageInfo },
                    },
                  },
                },
              });
            },
          });

        const groups = unclaimedGroups.map((group: UnclaimedGroup) =>
          translateUnclaimedGroup(group)
        );

        return props.children({ groups, handleFetchMore, loading, hasNextPage, refetch });
      }}
    </Query>
  );
}
